import React, { useEffect, useRef, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Rating,
  IconButton
} from '@mui/material';
import { 
  LocationOn, 
  Favorite, 
  FavoriteBorder,
  Visibility,
  Email
} from '@mui/icons-material';
import { getSupplierCategoryLabel } from '../../config/supplierTaxonomy';

const API_KEY = 'AIzaSyAz9gaQYQE6aJp2OXmxXbDfM5xZczs1W6Y';

const MapComponent = ({ suppliers, shortlistedSuppliers, onToggleShortlist, onViewProfile, onMessage, getCategoryColor, searchLocation }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);

  useEffect(() => {
    if (mapRef.current && !map) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: 54.5, lng: -3.0 }, // UK center
        zoom: 6, // Show all of UK
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      });
      setMap(newMap);
    }
  }, [map]);

  useEffect(() => {
    if (map && suppliers.length > 0) {
      // Clear existing markers
      markers.forEach(marker => marker.setMap(null));
      
      const newMarkers = suppliers.map(supplier => {
        if (!supplier.location || !supplier.location.coordinates || supplier.addressType === 'private') {
          return null;
        }

        const [lng, lat] = supplier.location.coordinates;
        
        const categoryColor = getCategoryColor(supplier.category);
        const borderColor = categoryColor + '4D'; // Add 30% opacity (4D in hex)
        
        const marker = new window.google.maps.Marker({
          position: { lat, lng },
          map: map,
          title: supplier.businessName,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="8" fill="${categoryColor}" stroke="${borderColor}" stroke-width="3"/>
              </svg>
            `)}`,
            scaledSize: new window.google.maps.Size(24, 24),
            anchor: new window.google.maps.Point(12, 12)
          }
        });

        marker.addListener('click', () => {
          setSelectedSupplier(supplier);
          // Don't use Google's info window - we'll use our custom popup
        });

        return marker;
      }).filter(Boolean);

      setMarkers(newMarkers);

      // Don't auto-fit bounds - keep UK default view unless user has selected a location
      // This ensures the map always shows the UK by default
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [map, suppliers]);

  // Auto-zoom to search location
  useEffect(() => {
    if (map && searchLocation && searchLocation.coordinates) {
      const [lng, lat] = searchLocation.coordinates;
      const currentCenter = map.getCenter();
      
      // Only update if the location has actually changed (avoid unnecessary updates)
      if (!currentCenter || 
          Math.abs(currentCenter.lat() - lat) > 0.0001 || 
          Math.abs(currentCenter.lng() - lng) > 0.0001) {
        map.setCenter({ lat, lng });
        map.setZoom(10); // Zoom out slightly to show surrounding areas
      }
    }
  }, [map, searchLocation]);


  return (
    <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
      <div ref={mapRef} style={{ height: '100%', width: '100%', borderRadius: '8px' }} />
      
      {/* Supplier Details Panel */}
      {selectedSupplier && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 16,
            left: 16,
            width: '90%',
            maxHeight: 'calc(100% - 32px)',
            overflow: 'auto',
            zIndex: 1000
          }}
        >
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>
                  {selectedSupplier.businessName}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => onToggleShortlist(selectedSupplier._id)}
                  color={shortlistedSuppliers.some(id => id.toString() === (selectedSupplier._id?.toString() || selectedSupplier._id)) ? 'error' : 'default'}
                >
                  {shortlistedSuppliers.some(id => id.toString() === (selectedSupplier._id?.toString() || selectedSupplier._id)) ? <Favorite /> : <FavoriteBorder />}
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: getCategoryColor(selectedSupplier.category) }}>
                  {getSupplierCategoryLabel(selectedSupplier.category)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  {selectedSupplier.priceRange}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Rating value={Math.min(Math.max(Number(selectedSupplier.rating) || 0, 0), 5)} precision={0.1} size="small" readOnly />
                <Typography variant="body2" color="text.secondary">
                  ({selectedSupplier.reviewCount || 0} reviews)
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <LocationOn fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    if (selectedSupplier.addressType === 'private') {
                      if (selectedSupplier.address) {
                        const parts = selectedSupplier.address.split(',').map(part => part.trim());
                        if (parts.length >= 2) {
                          const city = parts[parts.length - 2];
                          return `Based in ${city}`;
                        }
                      }
                      return 'Location not specified';
                    }
                    return selectedSupplier.address ? 
                      selectedSupplier.address.split(',').slice(-2).join(', ') : 
                      'Location not specified';
                  })()}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedSupplier.description ? 
                  (selectedSupplier.description.length > 75 ? 
                    selectedSupplier.description.substring(0, 75) + '...' : 
                    selectedSupplier.description) : 
                  'No description available.'
                }
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Visibility />}
                  onClick={() => onViewProfile(selectedSupplier._id)}
                >
                  View Profile
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Email />}
                  onClick={() => onMessage(selectedSupplier._id)}
                >
                  Message
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      )}
    </Box>
  );
};

const SupplierMap = ({ suppliers, shortlistedSuppliers, onToggleShortlist, onViewProfile, onMessage, getCategoryColor, searchLocation }) => {
  const render = (status) => {
    switch (status) {
      case Status.LOADING:
        return (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography>Loading map...</Typography>
          </Box>
        );
      case Status.FAILURE:
        return (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography color="error">Failed to load map</Typography>
          </Box>
        );
      case Status.SUCCESS:
        return (
          <MapComponent
            suppliers={suppliers}
            shortlistedSuppliers={shortlistedSuppliers}
            onToggleShortlist={onToggleShortlist}
            onViewProfile={onViewProfile}
            onMessage={onMessage}
            getCategoryColor={getCategoryColor}
            searchLocation={searchLocation}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Wrapper apiKey={API_KEY} render={render} />
  );
};

export default SupplierMap;
